import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AppkitService } from './components/shared/services/appkit.service';
import { WalletApiIntegrteService } from './components/shared/services/wallet-apiintegrte-service';
import { ToastrService } from './components/shared/services/toastr.service';

const walletGuard = async () => {
  const appkitService = inject(AppkitService);
  const router = inject(Router);
  const walletAuthService = inject(WalletApiIntegrteService);
  const toastr = inject(ToastrService);

  try {
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('AppKit initialization timeout')), 5000)
    );
    
    const initPromise = (async () => {
      await appkitService.ready();
      await appkitService.waitForConnectionResolve();
    })();

    await Promise.race([initPromise, timeoutPromise]);
  } catch (error) {
    console.error('WalletGuard: AppKit initialization failed, denying access');
    toastr.showToast('Wallet service unavailable. Please refresh and try again.', 'error');
    return router.createUrlTree(['/']);
  }

  const connected = appkitService.getCurrentWallet()?.connected;
  const validSession = walletAuthService.isValidAuthenticatedSession();

  if (!(connected && validSession)) {
    toastr.showToast('Please connect your wallet to access this page.', 'warning');
    return router.createUrlTree(['/']);
  }

  return true;
};

export const routes: Routes = [
  {
    // Marketing homepage — AQUALUX landing page
    path: '',
    loadComponent: () =>
      import('./pages/landing/landing').then(m => m.LandingPage),
  },
  {
    path: 'compliance',
    loadComponent: () =>
      import('./pages/compliance/compliance').then(m => m.CompliancePage),
  },
  {
    // Presale dApp (existing)
    path: 'app',
    canActivate: [walletGuard],
    loadComponent: () =>
      import('./components/home/home').then(m => m.Home),
  },
  {
    path: 'contribution-history',
    canActivate: [walletGuard],
    loadComponent: () =>
      import('./components/contribution-history/contribution-history').then(m => m.ContributionHistory)
  },
  {
    path: 'membership',
    loadComponent: () =>
      import('./components/landing/membership/membership').then(
        m => m.Membership
      ),
  },
  {
    path: 'wellness',
    loadComponent: () =>
      import('./components/landing/wellness/wellness').then(
        m => m.Wellness
      ),
  },
  {
    path: 'coming-soon',
    redirectTo: 'coming-soon/resort-vision',
    pathMatch: 'full',
  },
  {
    path: 'coming-soon/:section',
    loadComponent: () =>
      import('./components/shared/coming.soon/coming.soon').then(
        m => m.ComingSoon
      ),
  },
  {
    path: 'media',
    loadComponent: () =>
      import('./components/landing/media/media').then(
        m => m.MediaComponent
      ),
  },
  {
    path: 'governance',
    loadComponent: () =>
      import('./components/landing/governance/governance').then(
        m => m.Governance
      ),
  },
  {
    path: 'resort-vision',
    redirectTo: 'coming-soon/resort-vision',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: '',
  },
];

